<template>
  <div class="map-container" style="display: flex; flex-direction: column; height: 100%; width: 100%;">
    <!-- User Controls -->
    <div class="controls" style="position: sticky; top: 0; z-index: 1000;">
      <span
        v-for="(value, key) in filters"
        :key="key"
        @click="toggleFilter(key)"
        :class="{ 'active': filters[key], 'inactive': !filters[key] }"
        class="filter-icon"
      >
        <i class="material-icons">
          {{ getIcon(key) }}
        </i>
        {{ translateKey(key) }} ({{ counts[key] || 0 }})
      </span>
      <input type="text" v-model="searchQuery" v-bind:placeholder="$t('views.navbar.searchobjects')" @input="updateMap" class="search-input" />
    </div>

    <!-- Map -->
    <div id="mapid" v-observe-visibility="visibilityChanged" style="flex-grow: 1; position: relative;"></div>
  </div>
</template>

<script>
import UtilitiesService from '@/services/UtilitiesService'
import DashboardService from '@/services/DashboardService'
import AuthenticationService from '@/services/AuthenticationService'

export default {
  name: 'dashboardMap',
  props: ['railObjects', 'lastUpdated', 'companyId'],
  data () {
    return {
      currentUser: AuthenticationService.getUser(),
      railObjectIdsForCompany: [],
      selectedRailObjects: [],
      filters: {
        alarm: true,
        warning: true,
        ok: true,
        unknown: true,
        maintenance: true
      },
      searchQuery: '',
      mapInitialized: false,
      leafletMap: null,
      pruneCluster: null,
      clusterSize: null,
      railObjectByMarkerHashCode: {}
    }
  },
  beforeCreate: function () {},
  async created () {
    if (this.companyId) {
      const result = await DashboardService.getParentsByCompany(this.currentUser.UserId, this.companyId)
      this.railObjectIdsForCompany = result.data || []
    }
  },
  mounted () {
    this.initializeMap()
    this.$nextTick(() => {
      if (this.leafletMap) {
        this.leafletMap.on('dragstart', this.handleDragStart)
        this.leafletMap.on('dragend', this.handleDragEnd)
      }
    })
  },
  beforeDestroy () {
    this.leafletMap = null
    this.pruneCluster = null
    this.mapInitialized = false

    const mapElement = document.getElementById('mapid')
    if (mapElement) {
      mapElement.innerHTML = ''
    }
  },
  methods: {
    initializeMap () {
      if (this.leafletMap) {
        try {
          this.leafletMap.remove()
        } catch (error) {
        }
        this.leafletMap = null
      }

      const mapElement = document.getElementById('mapid')
      if (!mapElement) {
        return
      }

      const accesstoken = 'pk.eyJ1Ijoia2FyaW5sb29zIiwiYSI6ImNqO' +
        'GVhczRnNDBuMjkzM3FseXdybjd0Y2EifQ.NHM9Pss5TvDSIQZXIWktCA'

      const colors = ['#4CAF50', '#FF9800', '#F44336', '#2196F3']
      const pi2 = Math.PI * 2

      const latlng = L.latLng(52.09083, 5.12222)
      const tiles = L.tileLayer('https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token={accessToken}', {
        attribution: 'Strukton',
        minZoom: 2,
        maxZoom: 22,
        tileSize: 512,
        zoomOffset: -1,
        id: 'mapbox/streets-v11',
        accessToken: accesstoken
      })
      this.leafletMap = L.map(mapElement, {
        center: latlng,
        zoom: 7,
        layers: [tiles],
        maxBounds: [[-90, -180], [90, 180]],
        dragging: true,
        scrollWheelZoom: true,
        touchZoom: true,
        boxZoom: true,
        doubleClickZoom: true,
        zoomControl: true
      })

      if (this.leafletMap.dragging) {
        this.leafletMap.dragging.enable()
      }
      if (this.leafletMap.scrollWheelZoom) {
        this.leafletMap.scrollWheelZoom.enable()
      }
      if (this.leafletMap.zoomControl) {
        this.leafletMap.zoomControl.enable()
      }

      this.pruneCluster = new PruneClusterForLeaflet(120)
      const toggle = L.easyButton({
        states: [{
          stateName: 'add-markers',
          icon: '<i class="material-icons md-18">place</i>',
          title: 'markers',
          onClick: (control) => {
            this.clusterSize = -1
            this.pruneCluster.Cluster.Size = -1
            this.pruneCluster.ProcessView()
            control.state('remove-markers')
          }
        }, {
          icon: '<i class="material-icons md-18">refresh</i>',
          stateName: 'remove-markers',
          onClick: (control) => {
            this.clusterSize = 120
            this.pruneCluster.Cluster.Size = 120
            this.pruneCluster.ProcessView()
            control.state('add-markers')
          },
          title: 'cluster'
        }]
      })
      toggle.addTo(this.leafletMap)

      this.pruneCluster.BuildLeafletClusterIcon = function (cluster) {
        const e = new L.Icon.MarkerCluster()
        e.stats = cluster.stats
        e.population = cluster.population
        return e
      }

      this.pruneCluster.PrepareLeafletMarker = (leafletMarker, data) => {
        const railObject = this.railObjectByMarkerHashCode[data.markerHashCode]

        let statusdescription = ''
        if (railObject.OperationModeId === 5) {
          statusdescription = this.$t('maintenance')
        } else {
          switch (railObject.MonitorStatusId) {
            case 0:
              statusdescription = this.$t('unknown')
              break
            case 1:
              statusdescription = this.$t('normal')
              break
            case 2:
              statusdescription = this.$t('warning')
              break
            case 3:
              statusdescription = this.$t('error')
              break
            default:
              statusdescription = this.$t('unknown')
              break
          }
        }

        let assetPath = ''
        switch (railObject.DataSourceCode) {
          case 'WID':
            assetPath = 'switch'
            break
          case 'ATD':
            assetPath = 'axlecounter'
            break
          case 'AKD':
            assetPath = 'axleshunt'
            break
          case 'CDD':
            assetPath = 'mixedsignals'
            break
          default:
            assetPath = 'switch'
        }

        const statusclass = UtilitiesService.railObjectClass(railObject)
        data.popupContent = '<div class="infoDiv">' +
          '<h6 class="status-' + statusclass + '"><strong><a href="#/' + assetPath + '/' + railObject.RailObjectId + '">' + railObject.Name + '</a></strong></h6>' +
          '<strong>' + railObject.ComplexName + '</strong>' +
          '<p> ' + this.$t('views.dashboard.list.status') + '  : <span class="status-' + statusclass + '"><strong>' + statusdescription + '</strong></span></p>' +
          '<p> ' + this.$t('views.alarmlist.location') + ': ' + railObject.ComplexName + '</p>' +
          '<p> ' + this.$t('axlecounter.type') + '    : ' + railObject.ObjectCodeDescription + '</p>' +
          '</div>'

        railObject.leafletMarker = leafletMarker
        this.setIcon(railObject)
        if (leafletMarker.getPopup()) {
          leafletMarker.setPopupContent(data.popupContent)
        } else {
          leafletMarker.bindPopup(data.popupContent)
        }
      }

      L.Icon.MarkerCluster = L.Icon.extend({
        options: {
          iconSize: new L.Point(44, 44),
          className: 'prunecluster leaflet-markercluster-icon'
        },
        createIcon: function () {
          const e = document.createElement('canvas')
          this._setIconStyles(e, 'icon')
          const s = this.options.iconSize
          e.width = s.x
          e.height = s.y
          this.draw(e.getContext('2d'), s.x, s.y)
          return e
        },
        createShadow: function () {
          return null
        },
        draw: function (canvas, width, height) {
          var start = 0
          for (var i = 0, l = colors.length; i < l; ++i) {
            var size = this.stats[i] / this.population
            if (size > 0) {
              canvas.beginPath()
              canvas.moveTo(22, 22)
              canvas.fillStyle = colors[i]
              var from = start + 0.14
              var to = start + size * pi2
              if (to < from) {
                from = start
              }
              canvas.arc(22, 22, 22, from, to)
              start = start + size * pi2
              canvas.lineTo(22, 22)
              canvas.fill()
              canvas.closePath()
            }
          }
          canvas.beginPath()
          canvas.fillStyle = 'white'
          canvas.arc(22, 22, 18, 0, Math.PI * 2)
          canvas.fill()
          canvas.closePath()
          canvas.fillStyle = '#555'
          canvas.textAlign = 'center'
          canvas.textBaseline = 'middle'
          canvas.font = 'bold 12px sans-serif'
          canvas.fillText(this.population, 22, 22, 40)
        }
      })
      this.leafletMap.addLayer(this.pruneCluster)

      this.mapInitialized = true
      this.setRailObjects()

      this.$nextTick(() => {
        if (this.leafletMap) {
          this.leafletMap.invalidateSize()
        }
      })
    },
    handleDragStart () {
      const controls = this.$el.querySelector('.controls')
      if (controls) {
        controls.style.visibility = 'visible'
      }
    },
    handleDragEnd () {
      this.$forceUpdate()
      if (this.leafletMap) {
        this.leafletMap.invalidateSize()
      }
    },
    getIcon (filterKey) {
      const iconMap = {
        alarm: 'error',
        warning: 'warning',
        ok: 'check_circle',
        maintenance: 'build',
        unknown: 'help'
      }
      return iconMap[filterKey] || 'help'
    },
    toggleFilter (key) {
      this.filters[key] = !this.filters[key]
      this.updateMap()
    },
    visibilityChanged (isVisible) {
      if (this.leafletMap && isVisible) {
        this.leafletMap.invalidateSize()
      }
    },
    setRailObjects () {
      if (!this.pruneCluster || !this.mapInitialized || !this.leafletMap) {
        return
      }

      this.pruneCluster.RemoveMarkers()
      this.pruneCluster.ProcessView()

      const latLngs = []
      let filteredByCompany = this.railObjects
      if (this.companyId && this.railObjectIdsForCompany.length > 0) {
        filteredByCompany = this.railObjects.filter(railObject =>
          this.railObjectIdsForCompany.includes(railObject.RailObjectId)
        )
      }

      this.selectedRailObjects = filteredByCompany.filter(railObject => {
        if (railObject.OperationModeId === 5 && !this.filters.maintenance) return false
        if (railObject.MonitorStatusId === 0 && !this.filters.unknown) return false
        if (railObject.OperationModeId === 1) {
          if (railObject.MonitorStatusId === 1 && !this.filters.ok) return false
          if (railObject.MonitorStatusId === 2 && !this.filters.warning) return false
          if (railObject.MonitorStatusId === 3 && !this.filters.alarm) return false
        }

        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase()
          const name = railObject.Name || ''
          const complexName = railObject.ComplexName || ''
          return (
            name.toLowerCase().includes(query) ||
            complexName.toLowerCase().includes(query)
          )
        }
        return true
      })

      this.selectedRailObjects.forEach((railObject) => {
        this.addMarker(railObject)
        this.setIcon(railObject)
        if (railObject.Latitude != null && railObject.Longtitude != null) {
          latLngs.push(L.latLng(railObject.Latitude, railObject.Longtitude))
        }
      })

      if (latLngs.length > 0) {
        const bounds = L.latLngBounds(latLngs)
        this.leafletMap.fitBounds(bounds)
      }

      if (this.clusterSize) {
        this.pruneCluster.Cluster.Size = this.clusterSize
      }
      this.pruneCluster.ProcessView()

      if (this.leafletMap) {
        this.leafletMap.invalidateSize()
      }
    },
    updateMap () {
      this.setRailObjects()
    },
    addMarker (railObject) {
      const marker = new PruneCluster.Marker(railObject.Latitude, railObject.Longtitude, {})
      marker.data.markerHashCode = marker.hashCode
      this.railObjectByMarkerHashCode[marker.hashCode] = railObject
      if (railObject.OperationModeId === 1) {
        switch (railObject.MonitorStatusId) {
          case 1:
            marker.category = 0 // green
            break
          case 2:
            marker.category = 1 // orange
            break
          case 3:
            marker.category = 2 // red
            break
          default:
            marker.category = 3 // blue
            break
        }
      } else {
        marker.category = 3 // blue
      }

      railObject.marker = marker
      this.pruneCluster.RegisterMarker(marker)
    },
    setIcon (railObject) {
      if (!railObject.leafletMarker) {
        return
      }

      const leafletMarker = railObject.leafletMarker

      let iconUrl
      if (railObject.OperationModeId === 1) {
        switch (railObject.MonitorStatusId) {
          case 1:
            iconUrl = 'https://cdn.rawgit.com/pointhi/leaflet-color-markers' +
              '/master/img/marker-icon-2x-green.png'
            break
          case 2:
            iconUrl = 'https://cdn.rawgit.com/pointhi/leaflet-color-markers' +
              '/master/img/marker-icon-2x-orange.png'
            break
          case 3:
            iconUrl = 'https://cdn.rawgit.com/pointhi/leaflet-color-markers' +
              '/master/img/marker-icon-2x-red.png'
            break
          default:
            iconUrl = 'https://cdn.rawgit.com/pointhi/leaflet-color-markers' +
              '/master/img/marker-icon-2x-black.png'
            break
        }
      } else {
        iconUrl = 'https://cdn.rawgit.com/pointhi/leaflet-color-markers' +
          '/master/img/marker-icon-2x-blue.png'
      }

      leafletMarker.setIcon(L.icon({
        iconUrl: iconUrl,
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      }))
    },
    translateKey (key) {
      switch (key) {
        case 'alarm':
          return this.$t('alarm')
        case 'warning':
          return this.$t('warning')
        case 'ok':
          return this.$t('ok')
        case 'unknown':
          return this.$t('unknown')
        case 'maintenance':
          return this.$t('maintenance')
        default:
          return key.charAt(0).toUpperCase() + key.slice(1)
      }
    }
  },
  computed: {
    counts () {
      let filteredByCompany = this.railObjects
      if (this.companyId && this.railObjectIdsForCompany.length > 0) {
        filteredByCompany = this.railObjects.filter(railObject =>
          this.railObjectIdsForCompany.includes(railObject.RailObjectId)
        )
      }

      return {
        alarm: filteredByCompany.filter(obj => obj.MonitorStatusId === 3 && obj.OperationModeId === 1).length,
        warning: filteredByCompany.filter(obj => obj.MonitorStatusId === 2 && obj.OperationModeId === 1).length,
        ok: filteredByCompany.filter(obj => obj.MonitorStatusId === 1 && obj.OperationModeId === 1).length,
        unknown: filteredByCompany.filter(obj => obj.MonitorStatusId === 0).length,
        maintenance: filteredByCompany.filter(obj => obj.OperationModeId === 5).length
      }
    }
  },
  watch: {
    lastUpdated () {
      if (this.mapInitialized) {
        this.setRailObjects()
      }
    },
    companyId: {
      immediate: true,
      async handler (newCompanyId) {
        if (newCompanyId) {
          const result = await DashboardService.getParentsByCompany(this.currentUser.UserId, newCompanyId)
          this.railObjectIdsForCompany = result.data || []
        } else {
          this.railObjectIdsForCompany = []
        }
      }
    },
    railObjectIdsForCompany: {
      immediate: false,
      handler () {
        if (this.mapInitialized) {
          this.setRailObjects()
        }
      }
    }
  }
}
</script>

<style scoped>
.map-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.controls {
  position: sticky;
  top: 0;
  z-index: 1000;
  background: white;
  padding: 5px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  display: flex;
  gap: 5px;
  align-items: center;
  width: 100%;
  font-size: 12px;
  height: 34px;
  flex-wrap: nowrap;
  justify-content: space-between;
}

.controls .filter-icon {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 2px;
  white-space: nowrap;
}

.controls .material-icons {
  font-size: 21px;
}

.controls .active .material-icons {
  color: #000;
}

.controls .inactive .material-icons {
  color: #ccc;
}

.controls .search-input {
  padding: 0 5px;
  border: 1px solid #ccc;
  border-radius: 3px;
  font-size: 12px;
  height: 20px;
  line-height: 20px;
  width: 100px;
  vertical-align: middle;
  margin: 0;
}
#mapid {
  flex-grow: 1;
  width: 100%;
  height: calc(100% - 30px);
  position: relative;
  z-index: 1;
}

.leaflet-container {
  width: 100% !important;
  height: 100% !important;
  touch-action: none;
  cursor: default !important;
}

.leaflet-container.leaflet-grab {
  cursor: grab !important;
}

.leaflet-container.leaflet-grabbing {
  cursor: grabbing !important;
}

:global(.leaflet-container) {
  max-height: 100% !important;
  max-width: 100% !important;
}
</style>



// WEBPACK FOOTER //
// src/pages/dashboard2/DashboardMap.vue